import { useEffect, useRef } from "react";
import { io } from "socket.io-client";

/**
 * Keeps a single long-lived Socket.IO connection per user/origin.
 * Handlers are read from configRef on each event so parent re-renders do not
 * disconnect/reconnect the socket (which broke messaging on every keystroke).
 */
export default function useMessagesSocket(config) {
  const configRef = useRef(config);
  configRef.current = config;

  const socketRef = useRef(null);
  const { currentUserId, socketOrigin } = config;

  useEffect(() => {
    if (!currentUserId || !socketOrigin) return;

    const socket = io(`${socketOrigin}/friend-chat`, { withCredentials: true });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("user-online", { userId: currentUserId });
    });

    socket.on("friends-online-status", ({ onlineFriends: online = [] }) => {
      configRef.current.onFriendsOnlineStatus?.(new Set(online));
    });

    socket.on("friend-status-changed", ({ userId, isOnline }) => {
      configRef.current.onFriendStatusChanged?.({ userId, isOnline });
    });

    socket.on("new-message", (message) => {
      const c = configRef.current;
      c.onDirectMessage?.({
        message,
        selectedFriendId: c.selectedFriendIdRef.current,
        socket,
      });
    });

    socket.on("message-sent", (message) => {
      configRef.current.onDirectMessageSent?.(message);
    });

    socket.on("friend-typing", (payload) => {
      configRef.current.onFriendTyping?.(payload);
    });

    socket.on("new-group-message", (message) => {
      const c = configRef.current;
      c.onGroupMessage?.({
        message,
        selectedGroupId: c.selectedGroupIdRef.current,
      });
    });

    socket.on("group-member-typing", (payload) => {
      configRef.current.onGroupTyping?.(payload);
    });

    socket.on("group-member-left", (payload) => {
      configRef.current.onGroupMemberLeft?.(payload);
    });

    socket.on("group-was-disbanded", (payload) => {
      configRef.current.onGroupDisbanded?.(payload);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [currentUserId, socketOrigin]);

  return socketRef;
}
